import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/seo/SEO';
import { getBreadcrumbSchema, getOrganizationSchema } from '../lib/jsonLd';
import { client } from '../lib/sanity';
import { LoadingMessage } from '../components/common/LoadingMessage';
import { ErrorMessage } from '../components/common/ErrorMessage';
import type { FundingProject as FundingProjectType } from '../types/sanity';

type FundingStatus = 'current' | 'planned' | 'completed';

const SECTION_CONFIG: Record<FundingStatus, {
  title: string;
  description: string;
  badgeText: string;
  badgeClassName: string;
  dotClassName: string;
  activitiesLabel: string;
  emptyState: string;
}> = {
  current: {
    title: 'Aktualne dofinansowania',
    description: 'Projekty, które są realizowane i rozliczane w bieżącym roku.',
    badgeText: 'Realizujemy',
    badgeClassName: 'bg-accessible-gray-900 text-white',
    dotClassName: 'bg-emerald-400',
    activitiesLabel: 'Najważniejsze działania',
    emptyState: 'Obecnie nie realizujemy projektów współfinansowanych.'
  },
  planned: {
    title: 'Planowane projekty',
    description: 'Inicjatywy w przygotowaniu lub oczekujące na podpisanie umów.',
    badgeText: 'W przygotowaniu',
    badgeClassName: 'bg-accessible-gray-200 dark:bg-accessible-gray-800 high-contrast:bg-white/10 text-accessible-gray-900 dark:text-accessible-gray-100 high-contrast:text-white',
    dotClassName: 'bg-amber-400',
    activitiesLabel: 'Co planujemy zrobić',
    emptyState: 'Na ten moment nie planujemy nowych projektów współfinansowanych.'
  },
  completed: {
    title: 'Zakończone projekty',
    description: 'Projekty rozliczone w poprzednich latach. Dokumenty rozliczeniowe dostępne są w archiwum fundacji.',
    badgeText: 'Zrealizowano',
    badgeClassName: 'bg-accessible-gray-200 dark:bg-accessible-gray-800 high-contrast:bg-white/10 text-accessible-gray-800 dark:text-accessible-gray-100 high-contrast:text-white',
    dotClassName: 'bg-accessible-gray-500',
    activitiesLabel: 'Co udało się zrobić',
    emptyState: 'Brak zakończonych projektów do wyświetlenia.'
  }
};

const fetchFundingProjects = async () => {
  return client.fetch<FundingProjectType[]>(
    `*[_type == "fundingProject" && (isPublic != false)]
      | order(coalesce(orderRank, 9999) asc, fundingPeriod desc) {
        _id,
        title,
        status,
        financier,
        fundingPeriod,
        amount,
        description,
        activities,
        outcomes,
        note,
        attachments[]{
          label,
          "url": asset->url
        },
        orderRank
      }
    `
  );
};

const currencyFormatter = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
  maximumFractionDigits: 0
});

const formatAmount = (value?: number | null) => {
  if (typeof value !== 'number') {
    return null;
  }
  return currencyFormatter.format(value);
};

export function FundingPage() {
  const [projects, setProjects] = React.useState<FundingProjectType[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const loadFundingProjects = async () => {
      try {
        const data = await fetchFundingProjects();
        setProjects(data);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching funding projects:', err);
        setError('Nie udało się załadować listy dofinansowań. Spróbuj ponownie później.');
        setIsLoading(false);
      }
    };

    loadFundingProjects();
  }, []);

  const groupedProjects = React.useMemo(() => {
    return {
      current: projects.filter(project => project.status === 'current'),
      planned: projects.filter(project => project.status === 'planned'),
      completed: projects.filter(project => project.status === 'completed')
    } satisfies Record<FundingStatus, FundingProjectType[]>;
  }, [projects]);

  const renderProjectsSection = (status: FundingStatus) => {
    const section = SECTION_CONFIG[status];
    const sectionProjects = groupedProjects[status];

    return (
      <section className="mb-16" key={status}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
          <div>
            <h2 className="heading-3">{section.title}</h2>
            <p className="body-medium text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white">
              {section.description}
            </p>
          </div>
          <div
            className={`flex items-center gap-3 rounded-full px-4 py-2 shadow-md ${section.badgeClassName}`}
          >
            <span className={`h-2 w-2 rounded-full ${section.dotClassName}`} aria-hidden="true" />
            <span className="text-sm font-semibold tracking-wide uppercase">{section.badgeText}</span>
          </div>
        </div>

        {sectionProjects.length === 0 ? (
          <div className="bg-white dark:bg-accessible-gray-900 high-contrast:bg-black border border-dashed border-accessible-gray-200 dark:border-accessible-gray-700 high-contrast:border-white/40 rounded-2xl p-10 text-center">
            <p className="body-medium text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white">
              {section.emptyState}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {sectionProjects.map(project => {
              const amount = formatAmount(project.amount);
              const hasOutcomes = status === 'completed' && project.outcomes && project.outcomes.length > 0;
              const listItems = hasOutcomes ? project.outcomes : project.activities;
              const listLabel = hasOutcomes ? 'Co udało się zrobić' : section.activitiesLabel;
              const attachments = project.attachments?.filter(attachment => Boolean(attachment?.url)) ?? [];

              return (
                <article
                  key={project._id}
                  className="bg-white dark:bg-accessible-gray-900 high-contrast:bg-black border border-accessible-gray-100 dark:border-accessible-gray-800 high-contrast:border-white/20 rounded-2xl p-8 shadow-lg flex flex-col gap-6"
                >
                  <header>
                    <p className="label-small uppercase tracking-[0.25em] text-accessible-red-700">
                      {project.financier}
                    </p>
                    <h3 className="heading-4 mt-3">{project.title}</h3>
                    <p className="body-small text-accessible-gray-600 dark:text-white/90 high-contrast:text-white mt-2">
                      Okres realizacji: {project.fundingPeriod}
                    </p>
                    {amount && (
                      <p className="body-small text-accessible-gray-700 dark:text-white high-contrast:text-white mt-1">
                        Kwota dofinansowania: <span className="font-semibold">{amount}</span>
                      </p>
                    )}
                  </header>

                  <p className="body-medium text-accessible-gray-700 dark:text-white high-contrast:text-white">
                    {project.description}
                  </p>

                  {listItems && listItems.length > 0 && (
                    <div>
                      <h4 className="label-medium text-accessible-gray-700 dark:text-white high-contrast:text-white">
                        {listLabel}
                      </h4>
                      <ul className="mt-3 space-y-2 list-disc pl-5 text-accessible-gray-700 dark:text-white high-contrast:text-white">
                        {listItems.map(item => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {project.note && (
                    <p className="body-small text-accessible-gray-700 dark:text-white high-contrast:text-white bg-accessible-gray-50 dark:bg-accessible-gray-800/60 high-contrast:bg-black/40 border border-accessible-gray-100 dark:border-accessible-gray-700/60 high-contrast:border-white/20 rounded-xl p-4">
                      {project.note}
                    </p>
                  )}

                  {attachments.length > 0 && (
                    <div>
                      <h4 className="label-medium text-accessible-gray-700 dark:text-accessible-gray-200">Załączniki</h4>
                      <ul className="mt-3 space-y-2">
                        {attachments.map(attachment => (
                          <li key={attachment.url}>
                            <a
                              href={attachment.url}
                              target="_blank"
                              rel="noopener"
                              className="inline-flex items-center gap-2 text-accessible-red-700 hover:text-accessible-red-800 font-semibold underline underline-offset-4"
                            >
                              {attachment.label || 'Pobierz załącznik (PDF)'}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
    );
  };
  const jsonLd = React.useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@graph': [
        getOrganizationSchema(),
        getBreadcrumbSchema([
          { name: 'Strona główna', url: 'https://znajdki.pl/' },
          { name: 'Dofinansowania', url: 'https://znajdki.pl/dofinansowania' }
        ])
      ]
    }),
    []
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      <SEO
        title="Dofinansowania"
        description="Informacje o projektach finansowanych i współfinansowanych, dzięki którym Fundacja Znajdki może pomagać większej liczbie zwierząt."
        url="/dofinansowania"
        image="/images/partners/Warszawa-znak-RGB-kolorowy-projekt_wspolfinansuje.png"
        jsonLd={jsonLd}
      />

      <header className="mb-12 text-center md:text-left">
        <p className="subtitle uppercase tracking-[0.2em] text-accessible-red-700 mb-2">Transparentność</p>
        <h1 className="heading-1">Dofinansowania i projekty</h1>
        <p className="body-large mt-4 text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white max-w-3xl">
          Zewnętrzne środki finansowe pozwalają nam szybciej reagować na potrzeby zwierząt, prowadzić interwencje i
          realizować działania edukacyjne. Poniżej prezentujemy aktualne i zakończone projekty dofinansowane z funduszy
          publicznych oraz partnerstw.
        </p>
      </header>

      {/* W prostym języku */}
      <section className="mb-10">
        <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-6 rounded-xl shadow-lg">
          <h2 className="heading-4 no-margin">W skrócie</h2>
          <p className="body-medium no-margin mt-3">
            Dzięki dofinansowaniom możemy pokryć koszty leczenia, utrzymania i transportu zwierząt, a także prowadzić
            działania edukacyjne. Informacje o wydatkach publikujemy w zakładce{' '}
            <Link to="/sprawozdania" className="underline font-semibold">
              Sprawozdania
            </Link>
            .
          </p>
        </div>
      </section>

      {isLoading ? (
        <div className="py-20">
          <LoadingMessage ariaLabel="Ładowanie listy dofinansowań" />
        </div>
      ) : error ? (
        <div className="mb-16">
          <ErrorMessage message={error} ariaLabel="Błąd ładowania listy dofinansowań" />
        </div>
      ) : (
        <>
          {renderProjectsSection('current')}
          {groupedProjects.planned.length > 0 && renderProjectsSection('planned')}
          {renderProjectsSection('completed')}
        </>
      )}

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
        <div className="lg:col-span-2 bg-accessible-gray-50 dark:bg-accessible-gray-900 high-contrast:bg-black border border-accessible-gray-100 dark:border-accessible-gray-800 high-contrast:border-white/20 rounded-2xl p-8 shadow-lg flex flex-col">
          <h2 className="heading-3">Jak rozliczamy środki</h2>
          <p className="body-medium text-accessible-gray-700 dark:text-accessible-gray-200 high-contrast:text-white mt-4">
            Wszystkie dofinansowania rozliczamy zgodnie z umowami i obowiązującymi przepisami. Każdy wydatek jest
            dokumentowany fakturą lub rachunkiem, a sprawozdania merytoryczne i finansowe przekazujemy grantodawcom w
            terminach określonych w umowach. Najważniejsze dokumenty publikujemy publicznie, aby każdy mógł zapoznać się
            z wynikami naszych działań.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 lg:mt-auto">
            <Link
              to="/sprawozdania"
              className="inline-flex items-center gap-2 rounded-full bg-accessible-red-700 text-white px-5 py-2 font-semibold hover:bg-accessible-red-800 transition-colors"
            >
              Sprawdź sprawozdania
            </Link>
            <Link
              to="/statut"
              className="inline-flex items-center gap-2 rounded-full border border-accessible-gray-300 dark:border-accessible-gray-700 px-5 py-2 font-semibold text-accessible-gray-800 dark:text-accessible-gray-100 high-contrast:text-white hover:border-accessible-gray-500 dark:hover:border-accessible-gray-500 transition-colors"
            >
              Zobacz statut fundacji
            </Link>
          </div>
        </div>

        <div className="bg-white dark:bg-accessible-gray-900 high-contrast:bg-black border border-accessible-gray-100 dark:border-accessible-gray-800 high-contrast:border-white/20 rounded-2xl p-8 shadow-lg">
          <h2 className="heading-4">Kontakt w sprawie dofinansowań</h2>
          <p className="body-medium text-accessible-gray-700 dark:text-accessible-gray-200 high-contrast:text-white mt-4">
            Masz pytania dotyczące naszych projektów lub chcesz nawiązać współpracę? Skontaktuj się z nami.
          </p>
          <div className="mt-6 space-y-4 text-accessible-gray-700 dark:text-accessible-gray-200 high-contrast:text-white">
            <p className="body-medium">
              <span className="block font-semibold">Koordynacja projektów</span>
              Fundacja Znajdki
            </p>
            <p className="body-medium">
              <span className="block font-semibold">E-mail</span>
              <a href="mailto:znajdki2012@gmail.com" className="underline break-words">
                znajdki2012@gmail.com
              </a>
            </p>
            <p className="body-medium">
              <span className="block font-semibold">Telefon</span>
              <a href="tel:+48664821160" className="underline">
                +48 664 821 160
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}


